import React, { useId } from "react";
import styles from "./Notifications.scss";
import { NotificationLevel } from "netbank-shared/src/libs/models/Content/Notification";
import informationIcon from "~assets/information.svg";
import importantIcon from "~assets/important.svg";
import errorIcon from "~assets/warning-icon.svg";
import { DynamicLink } from "../DynamicLink/DynamicLink";
import { HtmlContent } from "../HtmlContent/HtmlContent";
import { CloseButton } from "../CloseButton/CloseButton";
import { tx } from "netbank-shared/src/libs/i18n";
import classNames from "classnames";
import { TrackingAction, TrackingCategory } from "netbank-shared/src/libs/models/Tracking";
import { ILink } from "netbank-shared/src/libs/models/Content/LinkItem";
import { Button } from "../Button/Button";
import { useStores } from "netbank-shared/src/hooks";
import { observer } from "mobx-react";

interface NotificationProps {
  level?: NotificationLevel;
  content?: string;
  header?: string;
  link?: ILink;
  button?: React.ComponentProps<typeof Button>;
  trackingCategory?: TrackingCategory;
  trackingAction?: TrackingAction;
  onClose?: () => void;
}

export const Notification = observer((props: NotificationProps) => {
  const {
    level = NotificationLevel.information,
    content,
    header,
    link,
    button,
    trackingCategory,
    trackingAction,
    onClose,
  } = props;
  const levelNameId = useId();
  const { uiStore } = useStores();

  const classes = [styles.notification];

  let icon = "";
  let levelText = "";

  switch (level) {
    case NotificationLevel.information:
      classes.push(styles.information);
      icon = informationIcon;
      levelText = tx("notification.information");
      break;
    case NotificationLevel.important:
      classes.push(styles.important);
      icon = importantIcon;
      levelText = tx("notification.important");
      break;
    case NotificationLevel.error:
      classes.push(styles.error);
      icon = errorIcon;
      levelText = tx("notification.warning");
      break;
    default:
      break;
  }

  return (
    <div className={classNames(classes)} role="region" aria-labelledby={levelNameId}>
      <div className={styles.wrapper}>
        <div className={styles.level}>
          <div className={styles.icon}>
            <img src={icon} alt="" />
            <h2 id={levelNameId}>{levelText}</h2>
          </div>
          {onClose && <CloseButton aria-label={tx("misc.closeNotifications")} onClick={onClose} dark />}
        </div>
        <div className={styles.innerWrapper}>
          <div className={styles.contentWrapper}>
            {header && <h3 className={styles.header}>{header}</h3>}
            {content && <HtmlContent html={content} />}
            {link && (
              <DynamicLink
                className={styles.link}
                to={link.href}
                target={link.target}
                trackingCategory={trackingCategory}
                trackingAction={trackingAction}
              >
                {link.text}
              </DynamicLink>
            )}
            {button?.title && (
              <Button
                fullWidth={uiStore.isMobile}
                {...button}
                className={classNames(button.className, styles.button)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
});
